<template>
  <div class="nodes-page admin-page">
    <header class="admin-page__header">
      <div class="admin-page__title">
        <h2>插件节点</h2>
        <p>「宝塔运营助手」插件保存设置后上报的服务器：公网 IP、系统、填写的 Cloak / VirusTotal 密钥与归属用户</p>
      </div>
      <a-button icon="reload" :loading="loading" @click="refresh">刷新</a-button>
    </header>

    <a-card :bordered="false" class="admin-card">
      <a-table
        :columns="columns"
        :data-source="rows"
        :rowKey="r => r.id"
        :loading="loading"
        :pagination="false"
        :scroll="{ x: 1280 }"
        :locale="tableLocale"
        size="middle"
        class="admin-table"
      >
        <template slot="ip" slot-scope="text">
          <span class="mono ip">{{ text }}</span>
        </template>

        <template slot="os" slot-scope="_, r">
          <span v-if="r.os_type" class="os-tag">{{ r.os_type }}<em v-if="r.os_version"> {{ r.os_version }}</em></span>
          <span v-else class="muted">—</span>
        </template>

        <template slot="url" slot-scope="text">
          <template v-if="text">
            <span class="mono ellip" :title="text">{{ text }}</span>
            <a-icon type="copy" class="copy-btn" title="复制" @click="copy(text)" />
          </template>
          <span v-else class="muted">—</span>
        </template>

        <template slot="key" slot-scope="text">
          <template v-if="text">
            <span class="mono ellip key" :title="text">{{ text }}</span>
            <a-icon type="copy" class="copy-btn" title="复制完整密钥" @click="copy(text)" />
          </template>
          <span v-else class="muted">未填写</span>
        </template>

        <template slot="owner" slot-scope="text">
          <span v-if="text">{{ text }}</span>
          <span v-else class="muted">匿名</span>
        </template>

        <template slot="ts" slot-scope="text">
          <span :title="text ? fullTime(text) : ''">{{ text ? relTime(text) : '—' }}</span>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<script>
import { listNodes } from '@/api/nodes';

export default {
  data() {
    return {
      loading: false,
      rows: [],
      columns: [
        { title: '服务器 IP', dataIndex: 'server_ip', scopedSlots: { customRender: 'ip' }, width: 140, fixed: 'left' },
        { title: '系统', scopedSlots: { customRender: 'os' }, width: 150 },
        { title: '主机名', dataIndex: 'hostname', width: 140, ellipsis: true },
        { title: '插件版本', dataIndex: 'plugin_version', width: 90 },
        { title: 'Cloak 地址', dataIndex: 'cloak_base_url', scopedSlots: { customRender: 'url' }, width: 220 },
        { title: 'Cloak Key', dataIndex: 'cloak_api_key', scopedSlots: { customRender: 'key' }, width: 200 },
        { title: 'VirusTotal Key', dataIndex: 'vt_api_key', scopedSlots: { customRender: 'key' }, width: 200 },
        { title: '归属用户', dataIndex: 'owner', scopedSlots: { customRender: 'owner' }, width: 110 },
        { title: '上报次数', dataIndex: 'report_count', width: 90, align: 'center' },
        { title: '最后上报', dataIndex: 'last_reported_at', scopedSlots: { customRender: 'ts' }, width: 130 },
      ],
    };
  },
  computed: {
    tableLocale() {
      const h = this.$createElement;
      return {
        emptyText: () =>
          h('div', { class: 'nodes-empty' }, [
            h('a-icon', { props: { type: 'cloud-server' }, class: 'nodes-empty__icon' }),
            h('p', { class: 'nodes-empty__title' }, '暂无节点上报'),
            h(
              'p',
              { class: 'nodes-empty__desc' },
              '服务器装上「宝塔运营助手」插件并在插件里保存一次设置后，该机器的 IP 与密钥会自动出现在这里。'
            ),
          ]),
      };
    },
  },
  mounted() {
    this.refresh();
  },
  methods: {
    refresh() {
      this.loading = true;
      listNodes()
        .then(res => {
          this.rows = res.data || [];
        })
        .catch(e => {
          this.$message.error((e && e.data && e.data.message) || '加载失败');
        })
        .finally(() => (this.loading = false));
    },
    copy(text) {
      const done = () => this.$message.success('已复制');
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(done).catch(() => this.fallbackCopy(text, done));
      } else {
        this.fallbackCopy(text, done);
      }
    },
    fallbackCopy(text, done) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); done(); } catch (e) { this.$message.error('复制失败'); }
      document.body.removeChild(ta);
    },
    fullTime(ts) {
      return new Date(ts * 1000).toLocaleString('zh-CN');
    },
    relTime(ts) {
      const diff = Date.now() / 1000 - ts;
      if (diff < 60) return '刚刚';
      if (diff < 3600) return Math.floor(diff / 60) + ' 分钟前';
      if (diff < 86400) return Math.floor(diff / 3600) + ' 小时前';
      if (diff < 86400 * 30) return Math.floor(diff / 86400) + ' 天前';
      return new Date(ts * 1000).toLocaleDateString('zh-CN');
    },
  },
};
</script>

<style scoped>
.mono {
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 12px;
}
.ip {
  font-weight: 600;
  color: #1f2d3d;
}
.ellip {
  display: inline-block;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
  color: #475569;
}
.key {
  color: #b45309;
}
.copy-btn {
  margin-left: 6px;
  color: #94a3b8;
  cursor: pointer;
  transition: color 0.15s;
}
.copy-btn:hover {
  color: #1890ff;
}
.muted {
  color: #b0b7c3;
}
.os-tag {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 4px;
  background: #eef2f7;
  color: #475569;
  font-size: 12px;
}
.os-tag em {
  font-style: normal;
  color: #94a3b8;
}
.nodes-empty {
  padding: 40px 16px;
  text-align: center;
}
.nodes-empty__icon {
  font-size: 44px;
  color: #cbd5e1;
  margin-bottom: 14px;
}
.nodes-empty__title {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 600;
  color: #64748b;
}
.nodes-empty__desc {
  margin: 0 auto;
  max-width: 460px;
  font-size: 13px;
  line-height: 1.6;
  color: #94a3b8;
}
</style>
